import { GraphQLVehicleGroupsRequester, VehicleGroupsRequester } from '@cobliteam/api-lib';
import type { VehiclesGroup } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { NewVehicleGroup, UUID, VehicleGroup, VehicleGroupWithPath } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { type UseQueryOptions, type UseSuspenseQueryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import useUser from '../useUser';

export const QUERY_KEY = 'VEHICLE_GROUP';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getVehicleGroupRequester = () => new VehicleGroupsRequester({ host: env.apiHost });
const getGraphQLVehicleGroupsRequester = () => new GraphQLVehicleGroupsRequester();

const useVehicleGroup = () => {
  const queryClient = useQueryClient();
  const { getSession } = useUser();
  const session = getSession();

  const fleetId = session.fleet.id;

  const invalidateGetQuery = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });

  const deleteVehicleGroup = useMutation<UUID | void, unknown, UUID>({
    mutationFn: (id) => getVehicleGroupRequester().deleteVehicleGroupFusca(id),
    onSuccess: () => invalidateGetQuery(),
  });

  const createVehicleGroup = useMutation<any, unknown, NewVehicleGroup>({
    mutationFn: async (newVehicleGroup) => getGraphQLVehicleGroupsRequester().createVehicleGroup(fleetId, newVehicleGroup),
  });

  const updateVehicleGroup = useMutation<any, unknown, VehicleGroup>({
    mutationFn: async (vehicleGroup) => getGraphQLVehicleGroupsRequester().updateVehicleGroup(fleetId, vehicleGroup),
  });

  const getVehicleGroups = (options?: Partial<UseQueryOptions<VehicleGroup[]>>, vehicleGroupsIds?: string[]) =>
    useQuery<VehicleGroup[]>({
      queryKey: [QUERY_KEY, fleetId, vehicleGroupsIds],
      ...options,
      queryFn: async () => await getGraphQLVehicleGroupsRequester().getVehicleGroups(vehicleGroupsIds),
      refetchOnMount: false,
    });

  const getVehicleGroupsWithPath = (options?: Partial<UseQueryOptions<VehicleGroupWithPath[]>>) =>
    useQuery<VehicleGroupWithPath[]>({
      queryKey: [QUERY_KEY, fleetId],
      ...options,
      queryFn: async () => await getGraphQLVehicleGroupsRequester().getVehicleGroupsWithPaths(),
      refetchOnMount: false,
    });

  const getVehicleGroupsWithUsers = (options?: Partial<UseQueryOptions<VehiclesGroup[]>>, groupIds?: string[]) =>
    useQuery<VehiclesGroup[]>({
      queryKey: [`${QUERY_KEY}`, 'WITH_USERS', fleetId, groupIds],
      ...options,
      queryFn: async () => await getGraphQLVehicleGroupsRequester().getVehicleGroupsWithUsers(groupIds),
      refetchOnMount: false,
    });

  const getVehicleGroupsWithUsersSuspense = (options?: Partial<UseSuspenseQueryOptions<VehiclesGroup[]>>, groupIds?: string[]) =>
    useSuspenseQuery<VehiclesGroup[]>({
      queryKey: [`${QUERY_KEY}`, 'WITH_USERS', fleetId, groupIds],
      ...options,
      queryFn: async () => await getGraphQLVehicleGroupsRequester().getVehicleGroupsWithUsers(groupIds),
      refetchOnMount: false,
    });

  return {
    createVehicleGroup,
    updateVehicleGroup,
    getVehicleGroups,
    getVehicleGroupsWithPath,
    getVehicleGroupsWithUsers,
    getVehicleGroupsWithUsersSuspense,
    deleteVehicleGroup,
  };
};

export default useVehicleGroup;
